import { EXPERIMENT_IDENTIFIERS } from '~server/routes/Pricing/experimentsConfig';

import { Packages } from '~shared/constants/pricing';
import { formatSummaryFeature } from '~pricing/lib/PackageFeatures';
import { SummaryFeatureList, SummaryFeatureRuleMap, FeatureListMap } from '~pricing/transformers/summary/types';

const FormsDefaultIndividualFeatureList: SummaryFeatureList = [
  'response_count',
  'everything_in',
  'grow_2529_question_types',
  'grow_2529_payment_qt',
  'grow_2529_website_embed',
  'grow_2529_zapier_integrations',
  'grow_2529_analyze_export_enabled',
  'grow_2529_analysis_filter',
  'grow_2529_file_upload',
  'skip_logic',
  'support',
  'statistical_significance',
];

const FormsRulesOverride: SummaryFeatureRuleMap = {
  grow_2529_forms_question_types: () => formatSummaryFeature('grow_2529_forms_question_types'), // form question types
  grow_2529_payment_qt: () => formatSummaryFeature('grow_2529_payment_qt'), // accept payments
  grow_2529_website_embed: () => formatSummaryFeature('grow_2529_website_embed'), // website embed
  grow_2529_zapier_integrations: () => formatSummaryFeature('grow_2529_zapier_integrations'), // 5000+ integrations with Zapier
  grow_2529_data_exports: () => formatSummaryFeature('grow_2529_data_exports'), // Data exports
  grow_2529_forms_analysis_filter: () => formatSummaryFeature('grow_2529_forms_analysis_filter'), // 1 analysis filter
  grow_2529_file_upload: () => formatSummaryFeature('grow_2529_file_upload'), // file upload
  skip_logic: () => formatSummaryFeature('grow_2529_skip_logic'), // skip logic only
  support: () => formatSummaryFeature('email_support'), // 24/7 customer support via email
};

const AdvantageAnnualFeatureList: SummaryFeatureList = [
  'response_count',
  'everything_in',
  'survey_branding', // Custom logo, colors, and URL
  'statistical_significance', // A/B testing & Statistical significance
  'quizzes_pro', // Quizzes with custom feedback
  'create_custom_variables_enabled', // Custom variables
  'benchmark_logic', // SurveyMonkey industry benchmarks
  'analyze_export_spss_enabled', // SPSS data exports
  'support',
];

const AdvantageAnnualRulesOverride: SummaryFeatureRuleMap = {
  everything_in: () => formatSummaryFeature(`everything_in_${Packages.AdvantageAnnual}`),
  quizzes_pro: () => formatSummaryFeature('grow_2529_quizzes_pro'), // override for tooltip
  survey_branding: () => formatSummaryFeature('grow_2529_survey_branding_t2'), // Custom logo, colors, and URL
  statistical_significance: () => formatSummaryFeature('grow_2529_statistical_significance'), // A/B testing & Statistical significance
  analyze_export_spss_enabled: () => formatSummaryFeature('grow_2529_analyze_export_spss_enabled_t2'), // SPSS data exports
  support: () => formatSummaryFeature('priority_email_support'), // 24/7 expedited email support
};

const PremierAnnualFeatureList: SummaryFeatureList = [
  'response_count',
  'everything_in',
  'collector_white_label_enabled', // white label surveys
  'extended_piping', // Advanced branching & piping, block randomization
  'crosstabs',
  'response_quality_enabled', // response quality
  'multilingual', // Multilingual surveys and forms
  'sentiment', // sentiment analysis
  'click_map_qt',
  'support', // phone support
];

const PremierAnnuaRulesOverride: SummaryFeatureRuleMap = {
  everything_in: () => formatSummaryFeature(`everything_in_${Packages.PremierAnnual}`),
  multilingual: () => formatSummaryFeature('grow_2529_multilingual'), // Multilingual surveys and forms
  sentiment: () => formatSummaryFeature('grow_2529_analyze_sentiment_enabled'), // sentiment analysis
  support: () => formatSummaryFeature('grow_2529_phone_support'),
  // overridden for tooltip:
  collector_white_label_enabled: () => formatSummaryFeature('grow_2529_collector_white_label_enabled'),
  grow_2529_payment_qt: (_, { dataCenter }) => {
    return dataCenter !== 'EU' ? formatSummaryFeature('grow_2529_payment_qt') : null;
  },
};

const StarterMonthlyFeatureList: SummaryFeatureList = [
  'response_count',
  'everything_in',
  'grow_2529_survey_quiz',
  'grow_2529_unlimited_analysis_filters',
  'statistical_significance',
];

const StarterRulesOverride: SummaryFeatureRuleMap = {
  // Add feature rule overrides below
  everything_in: () => formatSummaryFeature(`everything_in_${Packages.StarterAnnual}`),
  essential_question_types: () => formatSummaryFeature('grow_2529_survey_quiz'), // Survey & Quiz question types
  grow_2529_unlimited_analysis_filters: () => formatSummaryFeature('grow_2529_unlimited_analysis_filters'), // Unlimited analysis filters
  statistical_significance: () => formatSummaryFeature('grow_2529_text_analysis'), // Text analysis
  grow_2529_payment_qt: (_, { dataCenter }) => {
    return dataCenter !== 'EU' ? formatSummaryFeature('grow_2529_payment_qt') : null;
  },
};

const Grow2529FeatureListMapTreatment2: FeatureListMap = {
  [EXPERIMENT_IDENTIFIERS.GROW_2529_FORMS.treatment2]: {
    [Packages.AdvantageAnnual]: {
      list: AdvantageAnnualFeatureList,
      rules: AdvantageAnnualRulesOverride,
      includesDefaultList: true,
    },
    [Packages.StarterAnnual]: {
      list: StarterMonthlyFeatureList,
      rules: StarterRulesOverride,
      includesDefaultList: true,
    },
    [Packages.StandardMonthly]: {
      list: StarterMonthlyFeatureList,
      rules: StarterRulesOverride,
      includesDefaultList: true,
    },
    [Packages.PremierAnnual]: {
      list: PremierAnnualFeatureList,
      rules: PremierAnnuaRulesOverride,
      includesDefaultList: true,
    },
    [Packages.FormsAnnual]: {
      list: FormsDefaultIndividualFeatureList,
      rules: FormsRulesOverride,
      includesDefaultList: true,
    },
    [Packages.FormsMonthly]: {
      list: FormsDefaultIndividualFeatureList,
      rules: FormsRulesOverride,
      includesDefaultList: true,
    },
    [Packages.TeamAdvantage]: {
      list: [],
    },
    [Packages.TeamPremier]: {
      list: [],
    },
    [Packages.Enterprise]: {
      list: [],
    },
  },
};

export default Grow2529FeatureListMapTreatment2;
